import type { MobxDocument } from '../firestore-mobx'
import { empty, type FirestoreSlideRubric } from '../firestore/SlideRubric'
import type { FirebaseRepository } from './FirebaseRepository'
import {
  ObservableModelWithDecorators,
  emptyCollection,
  emptyModel,
} from '../firestore-mobx/model'
import { computed, makeObservable } from 'mobx'
import { RubricType } from '../firestore/PromptRubricType/types'
import { type SlideModel } from './SlideModel'

export class SlideRubric extends ObservableModelWithDecorators<FirestoreSlideRubric> {
  static empty(repository: FirebaseRepository) {
    return emptyModel(repository, this, empty)
  }

  static emptyCollection(repository: FirebaseRepository) {
    return emptyCollection(repository, this, empty)
  }

  constructor(
    repository: FirebaseRepository,
    doc: MobxDocument<FirestoreSlideRubric>
  ) {
    super(repository, doc)

    makeObservable(this)
  }

  @computed
  get rubricType(): RubricType | undefined {
    if (!this.hasData) return undefined
    // return index of the rubric type enum
    switch (this.data.rubricType) {
      case 0:
        return RubricType.blooms
      case 1:
        return RubricType.passFail
      case 2:
        return RubricType.decisionMaking
      case 3:
        return RubricType.evidence
      case 4:
        return RubricType.peerEngagement
      case 5:
        return RubricType.criticalThinking
      default:
        return undefined
    }
  }

  hasValidSlideConfiguration(slidesSorted: SlideModel[]):
    | { valid: true; reason?: never }
    | {
        valid: false
        reason: 'slideNoLongerExists' | 'endBeforeStart' | 'missingEnd'
      } {
    const { startSlideId, slideId: endSlideId } = this.data

    if (!endSlideId) {
      // all slides (valid config)
      if (!startSlideId) return { valid: true }

      // start with no end
      return { valid: false, reason: 'missingEnd' }
    }

    // get the slides by ID with position
    const startIndex = slidesSorted.findIndex((s) => s.id === startSlideId)
    const endIndex = slidesSorted.findIndex((s) => s.id === endSlideId)

    // pointing to slide that no longer exists
    if ((startSlideId && startIndex === -1) || endIndex === -1)
      return { valid: false, reason: 'slideNoLongerExists' }

    // end before start
    if (startSlideId && endIndex < startIndex)
      return { valid: false, reason: 'endBeforeStart' }

    // only end slide or start slide before or equal to end slide
    return { valid: true }
  }
}

/// Rubric Types
export class RubricTypeClass {
  /// Blooms
  static readonly blooms = RubricType.blooms
  /// Pass/Fail
  static readonly passFail = RubricType.passFail

  /// get rubricWeightingExponent
  /// the exponent used to weight the rubric score
  static getRubricWeightingExponent(type: RubricTypeClass): number {
    switch (type) {
      case RubricTypeClass.blooms:
        return 1.5

      case RubricTypeClass.passFail:
        return 1.5

      case RubricType.decisionMaking:
        return 1.5

      case RubricType.evidence:
        return 1.5

      case RubricType.peerEngagement:
        return 1.5

      case RubricType.criticalThinking:
        return 1.5

      default:
        throw new Error('Unknown RubricType')
    }
  }

  /// get rubricExpectedGroupScore
  /// the minimum score expected for this rubric type
  static getRubricExpectedGroupScore(type: RubricTypeClass): number {
    switch (type) {
      case RubricTypeClass.blooms:
        return 4

      case RubricTypeClass.passFail:
        return 1

      case RubricType.decisionMaking:
        return 4

      case RubricType.evidence:
        return 4

      case RubricType.peerEngagement:
        return 4

      case RubricType.criticalThinking:
        return 4

      default:
        throw new Error('Unknown RubricType')
    }
  }

  /// get rubricTypeNormalizationFactor
  /// the rubric type normalization factor
  /// used to allow for different rubric types to be compared
  /// to each other
  static getRubricTypeNormalizationFactor(type: RubricType): number {
    switch (type) {
      case RubricType.blooms:
        return 1

      case RubricType.passFail:
        return 4

      case RubricType.decisionMaking:
        return 1

      case RubricType.evidence:
        return 1

      case RubricType.peerEngagement:
        return 1

      case RubricType.criticalThinking:
        return 1

      default:
        throw new Error('Unknown RubricType')
    }
  }
}
